import * as types from '../types/authTypes';
import authService from '../../services/authService'; // Keep for getTokenFromCookie, getRefreshToken, isAuthenticated

// Initialize state without directly calling authService functions that might depend on the store
const initialState = {
  user: null, // User will be loaded asynchronously
  accessToken: authService.getTokenFromCookie(),
  refreshToken: authService.getRefreshToken(),
  isLoading: false,
  error: null,
  isAuthenticated: authService.isAuthenticated(),
  isSessionExpired: false,
  pendingInvitations: JSON.parse(sessionStorage.getItem('pendingInvitations') || '[]'), // Restore from sessionStorage
  deviceConflict: {
    hasConflict: false,
    deviceInfo: null,
    isChecking: false,
  },
  deviceLimit: {
    isAtLimit: false,
    activeSessions: [],
    maxDevices: 5,
    isLoading: false,
  },
};

const authReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.AUTH_LOGIN_REQUEST:
    case types.AUTH_REGISTER_REQUEST:
    case types.AUTH_RESET_PASSWORD_REQUEST:
    case types.AUTH_REQUEST_LOGIN_OTP_REQUEST:
    case types.AUTH_VERIFY_LOGIN_OTP_REQUEST:
    case types.AUTH_FORGOT_PASSWORD_REQUEST:
    case types.AUTH_VERIFY_REGISTRATION_OTP_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case types.AUTH_REFRESH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        accessToken: action.payload.accessToken,
        isAuthenticated: true,
        error: null,
      };
    case types.AUTH_LOGIN_SUCCESS:
    case types.AUTH_VERIFY_LOGIN_OTP_SUCCESS:
      return {
        ...state,
        isLoading: false,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        pendingInvitations: action.payload.pendingInvitations || [],
        isAuthenticated: true,
        error: null,
        isSessionExpired: false,
        deviceConflict: {
          hasConflict: false,
          deviceInfo: null,
          isChecking: false,
        },
      };
    case types.AUTH_REGISTER_SUCCESS:
    case types.AUTH_REQUEST_LOGIN_OTP_SUCCESS:
    case types.AUTH_FORGOT_PASSWORD_SUCCESS:
    case types.AUTH_RESET_PASSWORD_SUCCESS:
    case types.AUTH_VERIFY_REGISTRATION_OTP_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case types.AUTH_LOGIN_FAILURE:
    case types.AUTH_REGISTER_FAILURE:
    case types.AUTH_RESET_PASSWORD_FAILURE:
    case types.AUTH_REQUEST_LOGIN_OTP_FAILURE:
    case types.AUTH_VERIFY_LOGIN_OTP_FAILURE:
    case types.AUTH_FORGOT_PASSWORD_FAILURE:
    case types.AUTH_VERIFY_REGISTRATION_OTP_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case types.SESSION_EXPIRED:
      return {
        ...state,
        isSessionExpired: true,
        isAuthenticated: false,
      };
    case types.RESET_SESSION_EXPIRED:
      return {
        ...state,
        isSessionExpired: false,
      };
    case types.AUTH_CHECK_EXISTING_SESSION_REQUEST:
      return {
        ...state,
        deviceConflict: {
          ...state.deviceConflict,
          isChecking: true,
        },
      };
    case types.AUTH_CHECK_EXISTING_SESSION_SUCCESS:
      return {
        ...state,
        deviceConflict: {
          hasConflict: action.payload.hasExistingSession || false,
          deviceInfo: action.payload.deviceInfo || null,
          isChecking: false,
        },
      };
    case types.AUTH_CHECK_EXISTING_SESSION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        deviceConflict: {
          hasConflict: false,
          deviceInfo: null,
          isChecking: false,
        },
      };
    case types.AUTH_FORCE_LOGOUT_ALL_DEVICES_REQUEST:
      return {
        ...state,
        deviceConflict: {
          ...state.deviceConflict,
          isChecking: true,
        },
      };
    case types.AUTH_FORCE_LOGOUT_ALL_DEVICES_SUCCESS:
      return {
        ...state,
        deviceConflict: {
          hasConflict: false,
          deviceInfo: null,
          isChecking: false,
        },
      };
    case types.AUTH_FORCE_LOGOUT_ALL_DEVICES_FAILURE:
      return {
        ...state,
        deviceConflict: {
          ...state.deviceConflict,
          isChecking: false,
        },
      };
    case types.AUTH_LOGOUT:
      // Clear pendingInvitations from sessionStorage on logout
      sessionStorage.removeItem('pendingInvitations');
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        pendingInvitations: [],
        error: null,
        isSessionExpired: false,
        deviceConflict: {
          hasConflict: false,
          deviceInfo: null,
          isChecking: false,
        },
        deviceLimit: {
          isAtLimit: false,
          activeSessions: [],
          maxDevices: 5,
          isLoading: false,
        },
      };
    case types.AUTH_DEVICE_LIMIT_REACHED:
      return {
        ...state,
        isLoading: false,
        deviceLimit: {
          isAtLimit: true,
          activeSessions: action.payload.activeSessions || [],
          maxDevices: action.payload.maxDevices || 5,
          isLoading: false,
        },
      };
    case types.AUTH_LOGOUT_SPECIFIC_DEVICES_REQUEST:
      return {
        ...state,
        deviceLimit: {
          ...state.deviceLimit,
          isLoading: true,
        },
      };
    case types.AUTH_LOGOUT_SPECIFIC_DEVICES_SUCCESS:
      return {
        ...state,
        deviceLimit: {
          isAtLimit: false,
          activeSessions: [],
          maxDevices: 5,
          isLoading: false,
        },
      };
    case types.AUTH_LOGOUT_SPECIFIC_DEVICES_FAILURE:
      return {
        ...state,
        deviceLimit: {
          ...state.deviceLimit,
          isLoading: false,
        },
        error: action.payload,
      };
    case types.AUTH_CLEAR_DEVICE_LIMIT:
      return {
        ...state,
        deviceLimit: {
          isAtLimit: false,
          activeSessions: [],
          maxDevices: 5,
          isLoading: false,
        },
      };
    case types.SET_USER_DETAILS:
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        error: null,
        // Preserve pendingInvitations when updating user details
      };
    case types.REMOVE_PENDING_INVITATION:
      const updatedInvitations = state.pendingInvitations.filter(
        inv => inv.membershipId !== action.payload
      );
      return {
        ...state,
        pendingInvitations: updatedInvitations,
      };
    default:
      return state;
  }
};

export default authReducer;
